import { MarketplaceOfferJsonldMarketplaceOfferRead } from '@/api/generated';
import CounterInput from '@/atoms/forms/Counter';
import Spinner from '@/atoms/loader/Spinner';
import MoneyData from '@/atoms/MoneyData';
import CardThumb from '@/molecules/CardThumb';
import Metrics from '@/molecules/Metrics';
import { CURRENCY_API_CODE } from '@/types/modifiers/references';
import ModalClassicLayout from 'context/Modal/layout/Classic';
import { useFormatPrice } from 'hooks/i18n';
import useCardDetailsApi, { CardDetailsWithStats } from 'hooks/queries/cards/useCardsDetailsApi';
import { useCardOfferDetailApi } from 'hooks/queries/marketplace/offers';
import { useMyWallet } from 'hooks/queries/marketplace/wallet';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SIZE } from 'types/utils';
import useBuyOffer from './hooks';

const BuyOfferContent = ({
  card,
  closeModal,
  cardOffer,
}: {
  card: CardDetailsWithStats;
  closeModal: () => void;
  cardOffer: MarketplaceOfferJsonldMarketplaceOfferRead;
}) => {
  const { t } = useTranslation();
  const { formatUserCurrency, allFormat } = useFormatPrice();
  const formatOfferCurrency = allFormat[cardOffer.currency || CURRENCY_API_CODE.USD];
  const { formMethods, onSubmit, total, isLoading } = useBuyOffer({ closeModal, cardOffer });
  const {
    queryResult: { data: wallet },
  } = useMyWallet();

  return (
    <ModalClassicLayout
      closeModal={closeModal}
      isLoading={isLoading}
      onSubmit={onSubmit}
      submitLabel="buy.validate"
      title="buy"
    >
      <FormProvider {...formMethods}>
        <form className="py-0" name="create-cart" onSubmit={onSubmit}>
          <div className="flex flex-col items-center">
            <div className="flex flex-col gap-4">
              <CardThumb
                alt=""
                imagePath={card.imagePath}
                inMyCollection={card.stats?.inMyCollection}
                inMySale={card.stats?.inMySale}
                isExclusive={!!card.stats?.isExclusive}
                isSuspended={card.isSuspended}
                numberCopyAvailable={card.stats?.numberCopyAvailable}
                size={SIZE.LG}
              />
              <div>
                <CounterInput max={cardOffer.quantity} min={1} name="quantity" />
                <p className="text-center text-neutral-700">
                  {cardOffer.quantity} {t('available', { count: cardOffer.quantity })}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 py-4 grid grid-cols-2 gap-4">
            <p className="text-neutral-400 ">{t('unit-price')}</p>
            <p className="text-neutral-900 md-bold justify-self-end">{formatOfferCurrency(cardOffer.price || 0)}</p>
            {cardOffer.currency !== wallet?.currency && (
              <>
                <p className="text-neutral-400 ">{t('unit-price-converted')}</p>
                <p className="text-neutral-900 md-bold justify-self-end">
                  {formatUserCurrency(cardOffer.convertedPrice || 0)}
                </p>
              </>
            )}
            <p className="text-neutral-400 md-bold">{t('total')}</p>
            <p className="text-neutral-900 md-bold justify-self-end">{formatUserCurrency(total)}</p>
          </div>
          <Metrics icon="fa-solid fa-coin" subLine="wallet-balance">
            <MoneyData amount={wallet?.balance} currency={wallet?.currency} />
          </Metrics>
          <input className="hidden" type="submit" />
        </form>
      </FormProvider>
    </ModalClassicLayout>
  );
};

const BuyOffer = ({
  reference,
  closeModal,
  offerId,
}: {
  reference: string;
  closeModal: () => void;
  offerId?: string;
}) => {
  const { card, isLoading } = useCardDetailsApi(reference);
  const {
    queryResult: { isLoading: offerIsLoading, data },
  } = useCardOfferDetailApi({ cardRef: reference, offerId });

  // @ts-expect-error bugued api typed
  const cardOffer: MarketplaceOfferJsonldMarketplaceOfferRead = data?.data;

  if (isLoading || offerIsLoading || !card || !cardOffer) {
    return (
      <ModalClassicLayout closeModal={closeModal} title="buy">
        <Spinner size={SIZE.LG} />
      </ModalClassicLayout>
    );
  }

  return <BuyOfferContent card={card} cardOffer={cardOffer} closeModal={closeModal} />;
};

export default BuyOffer;
